"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const MapMarker = ({
  top,
  left,
  label
}: {
  top: string;
  left: string;
  label: string;
}) => <div className="absolute flex flex-col items-center group" style={{
  top,
  left
}}>
    <div className="relative flex items-center justify-center size-6">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/50 opacity-75"></span>
      <span className="relative inline-flex rounded-full size-3 bg-primary shadow-[0_0_10px_rgba(234,179,8,0.6)]"></span>
    </div>
    <span className="mt-1 text-[10px] font-medium tracking-wider uppercase text-primary/80 bg-background/80 px-1.5 py-0.5 rounded backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      {label}
    </span>
  </div>;
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="h-full flex flex-col items-center justify-center text-center p-8 border border-primary/20 rounded-xl bg-primary/5 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif text-white mb-2">Request Received</h3>
        <p className="text-muted-foreground mb-8 max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary/30 text-primary hover:bg-primary/10 hover:text-primary">
          Book Another Consultation
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs">Full Name</FormLabel>
              <FormControl>
                <input {...field} placeholder="Jane Doe" className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:ring-0 px-0 py-3 transition-colors placeholder:text-muted-foreground/30 text-foreground outline-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs">Email Address</FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="jane@example.com" className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:ring-0 px-0 py-3 transition-colors placeholder:text-muted-foreground/30 text-foreground outline-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs">Phone Number</FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="(02) 1234 5678" className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:ring-0 px-0 py-3 transition-colors placeholder:text-muted-foreground/30 text-foreground outline-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs">Company / Organisation</FormLabel>
              <FormControl>
                <input {...field} placeholder="Gold Star Executive Cleaning" className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:ring-0 px-0 py-3 transition-colors placeholder:text-muted-foreground/30 text-foreground outline-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {}
        <FormField control={control} name="service_interest" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs">Service of Interest</FormLabel>
              <FormControl>
                <select {...field} className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:ring-0 px-0 py-3 transition-colors text-foreground outline-none appearance-none cursor-pointer">
                  <option value="" disabled className="bg-card text-muted-foreground">Select a service</option>
                  {["Office Cleaning", "Strata Building Cleaning", "Gym Cleaning", "Childcare Centre Cleaning", "Custom Commercial Cleaning"].map(opt => <option key={opt} value={opt} className="bg-card text-foreground">{opt}</option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs">Preferred Date</FormLabel>
              <FormControl>
                <input {...field} type="date" className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:ring-0 px-0 py-3 transition-colors text-foreground outline-none [color-scheme:dark]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground uppercase tracking-wider text-xs">Additional Details</FormLabel>
            <FormControl>
              <textarea {...field} rows={3} placeholder="Any special requirements or questions?" className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:ring-0 px-0 py-3 transition-colors placeholder:text-muted-foreground/30 text-foreground outline-none resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] bg-primary text-primary-foreground hover:bg-primary/90 rounded-none h-12 text-base font-medium tracking-wide">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Processing...
            </> : "Request Quote"}
        </Button>
      </div>
    </Form>;
}
export default function SecureYourConsultation() {
  return <section id="secure-your-consultation" className="dark bg-background text-foreground py-20 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/10 to-transparent" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          
          {}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="mb-10">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-4 tracking-tight">
                Secure Your <span className="text-primary">Consultation</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-xl">
                Tell us about your cleaning needs. We provide tailored solutions for offices, strata, and commercial spaces across NSW.
              </p>
            </div>

            <FormProvider formId="consultation_form_home" sectionName="secure-your-consultation" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            company_name: "",
            service_interest: "",
            preferred_date: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            service_interest: {
              required: true
            }
          }} submitText="Request Quote" successMessage="Thank you for your inquiry. A Gold Star representative will contact you shortly to confirm your consultation.">
              <ConsultationForm />
            </FormProvider>
          </div>

          {}
          <div className="lg:col-span-5 flex flex-col gap-8">
            
            {}
            <div className="bg-card/5 border border-white/5 p-8 backdrop-blur-sm !w-[380.009px]">
              <h3 className="text-xl font-medium text-white mb-6 border-b border-white/10 pb-4">
                Contact Information
              </h3>
              <ul className="space-y-6">
                <li className="flex items-start gap-4 group">
                  <div className="size-10 rounded bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <Phone className="size-5 text-primary" />
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Call Us</span>
                    <Link to="tel:0412922689" className="text-lg text-white hover:text-primary transition-colors">
                      0412 922 689
                    </Link>
                  </div>
                </li>
                <li className="flex items-start gap-4 group">
                  <div className="size-10 rounded bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <Mail className="size-5 text-primary" />
                  </div>
                  <div className="!w-[300px]">
                    <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Email Us</span>
                    <Link to="mailto:cleaningwithgoldstar@gmail.com" className="text-lg text-white hover:text-primary transition-colors break-all">
                      cleaningwithgoldstar@gmail.com
                    </Link>
                  </div>
                </li>
                <li className="flex items-start gap-4 group">
                  <div className="size-10 rounded bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <Clock className="size-5 text-primary" />
                  </div>
                  <div>
                    <span className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Business Hours</span>
                    <span className="text-lg text-white">
                      Monday – Friday: 8am – 6pm
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            {}
            

          </div>
        </div>
      </div>
    </section>;
}